import { FC, useState } from "react";

import Box, { BoxProps } from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

import Heading from "sections/nfcTagsPage/Heading";
import ImageNext from "components/ImageNext";

import styles from "./styles";

export interface Tab {
  id: number;
  label: string;
  content: string | string[];
  image?: string;
  video?: string;
}

interface ProjectOverviewSectionProps {
  heading?: string;
  subheading?: string;
  tabs: Tab[];
  caseStudyFolder?: string;
  boxProps?: BoxProps;
}

const ProjectOverviewSection: FC<ProjectOverviewSectionProps> = ({ 
  heading = "PROJECT OVERVIEW",
  subheading = "Extend User Experience. Create New Revenue Streams. Bring Authenticity and Digitalization",
  tabs,
  caseStudyFolder = "pages/cases/puma",
  boxProps 
}) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));
  const [activeTabId, setActiveTabId] = useState(0);

  const handleChangeActiveTabId = (id: number) => {
    setActiveTabId(id);
  };

  const activeTab = tabs[activeTabId];

  return (
    <Box component="section" display="grid" {...boxProps}>
      <Container>
        <Heading
          heading={heading}
          subheading={subheading}
        />

        <Box sx={styles.contentWrapper}>
          <Box sx={styles.tabsWrapper}>
            {tabs.map((tab) => (
              <Accordion
                key={tab.id}
                expanded={!isDesktop || activeTabId === tab.id}
                sx={styles.accordion}
                onChange={() => handleChangeActiveTabId(tab.id)}
                disableGutters
              >
                <AccordionSummary sx={styles.accordionSummary}>
                  <Box
                    sx={(theme) =>
                      styles.bar(theme, {
                        isActive: activeTabId === tab.id,
                      })
                    }
                  />
                  <Typography
                    component="h4"
                    variant="h2"
                    sx={(theme) =>
                      styles.title(theme, {
                        isActive: activeTabId === tab.id,
                      })
                    }
                  >
                    {tab.label}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={styles.accordionDetails}>
                  <Box sx={styles.contentBox}>
                    {Array.isArray(tab.content) ? (
                      tab.content.map((paragraph, index) => (
                        <Typography key={index} variant="h4" sx={styles.text}>
                          {paragraph}
                        </Typography>
                      ))
                    ) : (
                      <Typography variant="h4" sx={styles.text}>
                        {tab.content}
                      </Typography>
                    )}
                  </Box>
                  
                  {!isDesktop && (tab.image || tab.video) && (
                    <Box sx={styles.mediaWrapper}>
                      {tab.video ? (
                        <video
                          autoPlay
                          loop
                          muted
                          playsInline
                          style={{
                            width: "100%",
                            height: "auto",
                            borderRadius: 8,
                          }}
                        >
                          <source
                            src={`${process.env.NEXT_PUBLIC_CLOUDFRONT_URL}/${caseStudyFolder}/${tab.video}`}
                            type="video/mp4"
                          />
                        </video>
                      ) : tab.image ? (
                        <ImageNext
                          src={`${process.env.NEXT_PUBLIC_CLOUDFRONT_URL}/${caseStudyFolder}/${tab.image}`}
                          width={800}
                          height={600}
                          alt={tab.label}
                          style={{
                            width: "100%",
                            height: "auto",
                            objectFit: "contain",
                            borderRadius: 8,
                          }}
                        />
                      ) : null}
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>

          {isDesktop && (activeTab.image || activeTab.video) && (
            <Box sx={styles.mediaWrapper}>
              {activeTab.video ? (
                <video
                  key={activeTab.id}
                  autoPlay
                  loop
                  muted
                  playsInline
                  style={{
                    width: "100%",
                    height: "auto",
                    borderRadius: 8,
                  }}
                >
                  <source
                    src={`${process.env.NEXT_PUBLIC_CLOUDFRONT_URL}/${caseStudyFolder}/${activeTab.video}`}
                    type="video/mp4"
                  />
                </video>
              ) : activeTab.image ? (
                <ImageNext
                  src={`${process.env.NEXT_PUBLIC_CLOUDFRONT_URL}/${caseStudyFolder}/${activeTab.image}`}
                  width={800}
                  height={600}
                  alt={activeTab.label}
                  style={{
                    width: "100%",
                    height: "auto",
                    objectFit: "contain",
                    borderRadius: 8,
                  }}
                />
              ) : null}
            </Box>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default ProjectOverviewSection;

